import {
  CloseIcon,
  DialogContentWrapper,
  DialogExit,
  DialogHeaderContentWrapper,
  DialogHeaderWrapper,
  DialogMainText,
  DialogSubText,
  DialogWrapper,
} from '@/modules/pmt/components/Dialog/Dialog.styles';
import Image from 'next/image';
import { Dialog, VisuallyHidden } from 'radix-ui';
import type { ReactNode } from 'react';
import { DialogContentPositioner, DialogOverlay, DialogTrigger } from './RadixDialog.styles';

type TRadixDialogProps = {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
};

type TRadixDialogHeaderProps = {
  children?: ReactNode;
  closeable?: boolean;
  onClose?: () => void;
};

const RadixDialogHeader = ({ children, closeable = false, onClose }: TRadixDialogHeaderProps) => {
  return (
    <DialogHeaderWrapper>
      <Image aria-hidden='true' src='/images/verify-graphic.svg' alt='Dialog header graphic' fill sizes='100vw' />
      {closeable && (
        <Dialog.Close asChild>
          <DialogExit onClick={onClose} aria-label='Close Dialog'>
            <CloseIcon aria-hidden='true' />
          </DialogExit>
        </Dialog.Close>
      )}
      <DialogHeaderContentWrapper>{children}</DialogHeaderContentWrapper>
    </DialogHeaderWrapper>
  );
};

const RadixDialogContent = ({ children }: { children: ReactNode }) => {
  return <DialogContentWrapper>{children}</DialogContentWrapper>;
};

const RadixDialog = ({ children, open, onOpenChange, trigger }: TRadixDialogProps) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger>{trigger}</DialogTrigger>}
      <Dialog.Portal>
        <DialogOverlay />
        <DialogContentPositioner aria-describedby={undefined}>
          <VisuallyHidden.Root asChild>
            <Dialog.Title>Dialog</Dialog.Title>
          </VisuallyHidden.Root>
          {children}
        </DialogContentPositioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const WithGraphicWrapper = ({ children, ...props }: TRadixDialogProps) => {
  return (
    <RadixDialog {...props}>
      <DialogWrapper>{children}</DialogWrapper>
    </RadixDialog>
  );
};

RadixDialog.Header = RadixDialogHeader;
RadixDialog.Content = RadixDialogContent;
RadixDialog.MainText = DialogMainText;
RadixDialog.SubText = DialogSubText;
RadixDialog.Close = Dialog.Close;
RadixDialog.WithGraphicWrapper = WithGraphicWrapper;

export { RadixDialog };
